/**
 * Keeps the Seat Firmness Upgrade Charge product (id 15391820873841, Tier
 * variants 67585452966001 / 67585452998769) in sync with the sofa line(s) it
 * belongs to, wherever the cart is edited (drawer, cart page, or a stale
 * state carried over from an earlier visit).
 *
 * Each upgrade line carries an `Associated with` property naming the exact
 * sofa product it belongs to (set by seat-firmness-options.js, same pattern
 * matching-accessories.js uses for generic accessories) — this is what makes
 * it possible to support several different sofas in the same cart, each with
 * their own "Medium to firm" selection and their own upgrade line, without
 * them merging into one and without one sofa's change affecting another's.
 *
 * On load (covers a cart already stale from an earlier visit or a non-AJAX
 * change) and on every theme `cart:update` event (fired after any cart add/
 * change/remove — see assets/events.js), every distinct sofa title that
 * shows up either on a "Medium to firm" sofa line or as an upgrade line's
 * `Associated with` value is reconciled:
 *
 * - Required qty (sum of that title's "Medium to firm" sofa line quantities)
 *   vs. actual qty (sum of that title's upgrade line quantities) is compared.
 * - Sofa qty increased/decreased in the cart → the upgrade line's quantity is
 *   adjusted to match via /cart/change.js.
 * - Sofa removed entirely (required qty 0) → its upgrade line(s) are removed
 *   too (quantity set to 0).
 * - Upgrade line removed entirely (actual qty 0) → the "Medium to firm"
 *   property is cleared from the sofa's line (every other property already
 *   on the line — Fabric, Colour, Chaise Side, etc. — is left untouched).
 *
 * Separately, if every remaining line in a non-empty cart is an upgrade
 * line (e.g. one added with no association, or every other product already
 * removed), the whole cart is cleared via /cart/clear.js.
 *
 * Matches sofa lines by property VALUE ("Medium to firm") rather than by
 * property key, since the key is a merchant-editable block setting (default
 * "Seat Firmness") — this way the sync keeps working even if that label is
 * renamed.
 *
 * No page reload: once every fix is applied, a real `cart:update` event
 * (same name/shape every other cart action already dispatches — see
 * assets/events.js) is fired on `document`. The theme's own
 * `cart-items-component` reacts to it like any other cart change and
 * re-renders its section via the Section Rendering API (`sectionRenderer` in
 * assets/section-renderer.js, used automatically when the event carries no
 * pre-rendered `sections` HTML), and `cart-icon` updates the header badge
 * from the event's `itemCount`. Both are existing theme behavior, not
 * anything new added here.
 */
(function () {
  var UPGRADE_VARIANT_IDS = ['67585452966001', '67585452998769'];
  var ASSOCIATION_PROPERTY_KEY = 'Associated with';
  var STALE_PROPERTY_VALUE = 'Medium to firm';
  var isSyncing = false;

  function isUpgradeItem(item) {
    return UPGRADE_VARIANT_IDS.indexOf(String(item.variant_id)) !== -1;
  }

  function cartHasOnlyUpgradeItems(cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.length > 0 && cart.items.every(isUpgradeItem));
  }

  function sumQuantity(lines) {
    return lines.reduce(function (total, line) {
      return total + line.quantity;
    }, 0);
  }

  /**
   * Groups cart lines by sofa title: every "Medium to firm" sofa line and
   * every upgrade line `Associated with` that same title land in one bucket.
   */
  function buildAssociationMap(cart) {
    var map = {};

    function bucket(title) {
      if (!map[title]) map[title] = { sofaLines: [], upgradeLines: [] };
      return map[title];
    }

    cart.items.forEach(function (item, index) {
      if (isUpgradeItem(item)) {
        var assoc = item.properties && item.properties[ASSOCIATION_PROPERTY_KEY];
        if (!assoc) return;
        bucket(assoc).upgradeLines.push({ line: index + 1, quantity: item.quantity });
        return;
      }

      var properties = item.properties || {};
      var staleKey = null;
      for (var key in properties) {
        if (Object.prototype.hasOwnProperty.call(properties, key) && properties[key] === STALE_PROPERTY_VALUE) {
          staleKey = key;
          break;
        }
      }
      if (!staleKey) return;

      bucket(item.product_title).sofaLines.push({
        line: index + 1,
        key: staleKey,
        quantity: item.quantity,
        properties: properties,
      });
    });

    return map;
  }

  /**
   * @returns {{ propertyClears: object[], quantityChanges: {line:number, quantity:number}[] }}
   */
  function planCartFixes(cart) {
    var map = buildAssociationMap(cart);
    var propertyClears = [];
    var quantityChanges = [];

    Object.keys(map).forEach(function (title) {
      var group = map[title];
      var requiredQty = sumQuantity(group.sofaLines);
      var actualQty = sumQuantity(group.upgradeLines);

      if (requiredQty === actualQty) return;

      if (requiredQty === 0) {
        group.upgradeLines.forEach(function (u) {
          quantityChanges.push({ line: u.line, quantity: 0 });
        });
        return;
      }

      if (actualQty === 0) {
        group.sofaLines.forEach(function (s) {
          propertyClears.push(s);
        });
        return;
      }

      // Both sides exist but disagree in quantity (sofa qty changed in
      // cart) — resync the first upgrade line to match, dropping any extra
      // duplicate upgrade lines for the same title.
      quantityChanges.push({ line: group.upgradeLines[0].line, quantity: requiredQty });
      for (var i = 1; i < group.upgradeLines.length; i++) {
        quantityChanges.push({ line: group.upgradeLines[i].line, quantity: 0 });
      }
    });

    return { propertyClears: propertyClears, quantityChanges: quantityChanges };
  }

  function getChangeUrl() {
    return (window.Theme && Theme.routes && Theme.routes.cart_change_url) || '/cart/change.js';
  }

  function clearOrphanedProperty(orphan) {
    var newProperties = {};
    for (var key in orphan.properties) {
      if (Object.prototype.hasOwnProperty.call(orphan.properties, key) && key !== orphan.key) {
        newProperties[key] = orphan.properties[key];
      }
    }

    return fetch(getChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: orphan.line, quantity: orphan.quantity, properties: newProperties }),
    });
  }

  function changeLineQuantity(line, quantity) {
    return fetch(getChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    });
  }

  function clearWholeCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
  }

  /**
   * Re-fetches the cart and broadcasts a `cart:update` event so the cart
   * drawer/page and header badge refresh in place — no page reload. Uses the
   * same event name/shape every other cart action in this theme already
   * dispatches (see assets/events.js), so the existing listeners handle it:
   * `cart-items-component` re-renders its section via the Section Rendering
   * API (assets/section-renderer.js) since no pre-rendered `sections` HTML is
   * included, and `cart-icon` updates the header badge from `itemCount`.
   */
  function notifyCartUpdated() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            detail: {
              resource: cart,
              sourceId: 'seat-firmness-upgrade-sync',
              data: { itemCount: cart.item_count, source: 'seat-firmness-upgrade-sync' },
            },
          })
        );
      });
  }

  function syncCart(cart) {
    if (isSyncing || !cart || !Array.isArray(cart.items)) return;

    if (cartHasOnlyUpgradeItems(cart)) {
      isSyncing = true;
      clearWholeCart()
        .then(notifyCartUpdated)
        .catch(function (error) {
          console.error('seat-firmness-upgrade-sync:', error);
        })
        .finally(function () {
          isSyncing = false;
        });
      return;
    }

    var fixes = planCartFixes(cart);
    // Quantity-0 changes remove their line and shift every later line's
    // index, so they must run last, and in descending line order among
    // themselves, so each removal only ever affects indices already handled.
    var nonRemovalChanges = fixes.quantityChanges.filter(function (change) {
      return change.quantity > 0;
    });
    var removals = fixes.quantityChanges
      .filter(function (change) {
        return change.quantity === 0;
      })
      .sort(function (a, b) {
        return b.line - a.line;
      });

    if (fixes.propertyClears.length === 0 && nonRemovalChanges.length === 0 && removals.length === 0) return;

    isSyncing = true;
    var chain = Promise.resolve();

    fixes.propertyClears.forEach(function (orphan) {
      chain = chain.then(function () {
        return clearOrphanedProperty(orphan);
      });
    });
    nonRemovalChanges.forEach(function (change) {
      chain = chain.then(function () {
        return changeLineQuantity(change.line, change.quantity);
      });
    });
    removals.forEach(function (change) {
      chain = chain.then(function () {
        return changeLineQuantity(change.line, 0);
      });
    });

    chain
      .then(notifyCartUpdated)
      .catch(function (error) {
        console.error('seat-firmness-upgrade-sync:', error);
      })
      .finally(function () {
        isSyncing = false;
      });
  }

  document.addEventListener('cart:update', function (event) {
    var cart = event.detail && event.detail.resource;
    if (cart && Array.isArray(cart.items)) syncCart(cart);
  });

  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(function (response) {
      return response.json();
    })
    .then(syncCart)
    .catch(function () {});
})();
